import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    localStorage.setItem('cookie_consent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(onlyNecessary);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowPreferences(false);
  };

  const togglePreference = (key: keyof typeof preferences) => {
    if (key === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-cookie-line text-[#D4AF37] text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0A381F] mb-2">We Value Your Privacy</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or reject non-essential cookies.
                  </p>
                </div>
              </div>
              <Link 
                to="/privacy" 
                className="text-sm text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer inline-flex items-center gap-1"
              >
                Read our Privacy Policy
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base"></i>
                </div>
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-[#0A381F] rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
              >
                Customize
              </button>
              <button
                onClick={handleRejectAll}
                className="px-6 py-3 bg-gray-100 text-[#0A381F] rounded-lg font-semibold hover:bg-gray-200 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg font-semibold hover:bg-[#C19B2F] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#0A381F]">Cookie Preferences</h2>
              <button
                onClick={() => setShowPreferences(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <div className="px-8 py-6 space-y-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                We use different types of cookies to optimize your experience on our platform. 
                You can choose which categories you want to allow. Please note that blocking some types of cookies may impact your experience.
              </p>

              {/* Necessary Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#0A381F] mb-2">Strictly Necessary Cookies</h3>
                    <p className="text-sm text-gray-600">
                      These cookies are essential for the website to function properly. They enable core functionality such as security, 
                      network management, and accessibility. You cannot opt-out of these cookies.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-gray-300 rounded-full flex items-center px-1 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white rounded-full shadow-md transform translate-x-6"></div>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-3">
                  <strong>Always Active</strong>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#0A381F] mb-2">Functional Cookies</h3>
                    <p className="text-sm text-gray-600">
                      These cookies enable enhanced functionality and personalization, such as remembering your preferences, 
                      language settings, and region.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => togglePreference('functional')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                        preferences.functional ? 'bg-[#D4AF37]' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${
                        preferences.functional ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#0A381F] mb-2">Analytics Cookies</h3>
                    <p className="text-sm text-gray-600">
                      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. 
                      This helps us improve our services.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                        preferences.analytics ? 'bg-[#D4AF37]' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${
                        preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#0A381F] mb-2">Marketing Cookies</h3>
                    <p className="text-sm text-gray-600">
                      These cookies are used to track visitors across websites to display relevant advertisements. 
                      They help us measure the effectiveness of our marketing campaigns.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                        preferences.marketing ? 'bg-[#D4AF37]' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${
                        preferences.marketing ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-8 py-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRejectAll}
                className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-[#0A381F] rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Reject All
              </button>
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-6 py-3 bg-[#D4AF37] text-white rounded-lg font-semibold hover:bg-[#C19B2F] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
